import { Box, Typography, TextareaAutosize } from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './SubmissionDetails.css';

const TechnicalConceptsTab = ({ data, feedbackState, onFeedbackChange, readOnly, isAdmin }) => {
    const concepts = data?.evaluations || [];
    const perfSummary = data?.conceptPerfSummary;

    if (concepts.length === 0) {
        return (
            <Box sx={{ padding: '40px 20px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #ced4da' }}>
                <Typography sx={{ color: '#6c757d', fontSize: '15px', fontWeight: 500 }}>No technical concepts available.</Typography>
            </Box>
        );
    }

    return (
        <Box>
            {concepts.map((concept, index) => (
                <Box key={concept.evaluationId || index} className="concept-card" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '24px', backgroundColor: '#fff', overflow: 'hidden' }}>
                    <Box className="concept-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', backgroundColor: '#f8f9fa', padding: '14px 24px', borderBottom: '1px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, paddingRight: '24px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <PsychologyIcon fontSize="small" sx={{ color: '#1976d2', mt: '2px' }} />
                                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: '#495057', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap', mt: '3px' }}>
                                    CONCEPT {index + 1}
                                </Typography>
                            </Box>
                            <Box sx={{ ml: '30px', mt: '4px' }}>
                                <Typography component="div" sx={{ fontSize: '15px', fontWeight: 700, color: '#212529', lineHeight: 1.5, wordBreak: 'break-word' }}>
                                    {concept.question || concept.questionText || "No question provided."}
                                </Typography>
                            </Box>
                        </Box>

                    </Box>
                    <Box className="concept-body" sx={{ padding: '24px 28px' }}>
                        {concept.type === 'concept_text' ? (
                            <Box sx={{ marginBottom: '24px' }}>
                                <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>
                                    SUBMITTED TEXT ANSWER
                                </Typography>
                                <Box className="concept-answer" sx={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #64b5f6', borderRadius: '0 6px 6px 0', padding: '18px 22px', marginBottom: 0, fontStyle: concept.textAnswer ? 'normal' : 'italic', color: '#495057', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                    {concept.textAnswer ? `"${concept.textAnswer}"` : "No text answer submitted."}
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>
                                    TRANSCRIPTION
                                </Typography>
                                <Box className="concept-answer" sx={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #64b5f6', borderRadius: '0 6px 6px 0', padding: '18px 22px', marginBottom: '24px', fontStyle: 'italic', color: '#495057', fontSize: '14px', lineHeight: 1.6 }}>
                                    "{concept.transcription || "No transcription available."}"
                                </Box>

                                <Box sx={{ marginBottom: '24px' }}>
                                    <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px', display: 'block' }}>
                                        RECORDED AUDIO
                                    </Typography>
                                    {concept.audioFileUrl ? (
                                        <Box sx={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: '8px', 
                                            padding: '10px 16px', 
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)' 
                                        }}>
                                            <audio controls src={concept.audioFileUrl} style={{ width: '100%', height: '40px', outline: 'none' }}>
                                                Your browser does not support the audio element.
                                            </audio>
                                        </Box>
                                    ) : (
                                        <Box sx={{ 
                                            backgroundColor: '#f8f9fa', 
                                            border: '1px dashed #ced4da', 
                                            borderRadius: '8px', 
                                            padding: '16px', 
                                            textAlign: 'center' 
                                        }}>
                                            <Typography sx={{ fontSize: '13.5px', color: '#6c757d', fontStyle: 'italic' }}>
                                                No audio recording available for this question.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </>
                        )}


                        {concept.feedbackText && (
                            <Box sx={{ marginBottom: '24px' }}>
                                <Typography component="div" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px', display: 'block' }}>
                                    AI FEEDBACK
                                </Typography>
                                <Typography component="div" sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6, display: 'block' }}>
                                    {concept.feedbackText}
                                </Typography>
                            </Box>
                        )}
                        <Typography component="div" className="concept-comment-label" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px', display: 'block' }}>
                            MENTOR FEEDBACK
                        </Typography>
                        <TextareaAutosize
                            key={`concept-feedback-${concept.evaluationId || index}`}
                            minRows={1}
                            className={`concept-comment-area ${readOnly && !concept.mentorEvaluation?.feedback ? 'empty-readonly' : ''}`}
                            value={feedbackState?.[concept.evaluationId]?.feedback !== undefined ? feedbackState[concept.evaluationId].feedback : (concept.mentorEvaluation?.feedback || "")}
                            disabled={readOnly}
                            onChange={(e) => onFeedbackChange && onFeedbackChange(concept.evaluationId, undefined, e.target.value)}
                            placeholder={readOnly ? "No feedback provided." : "Provide your feedback here..."}
                            style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '6px', padding: '16px', fontSize: '14px', color: readOnly ? '#6c757d' : '#343a40', backgroundColor: readOnly ? '#e9ecef' : '#f8f9fa', lineHeight: 1.6, fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box', overflow: 'hidden' }}
                        />
                        {(isAdmin && Boolean(concept.mentorEvaluation?.justification)) && (
                            <Box sx={{ mt: 3 }}>
                                <Typography component="div" className="concept-comment-label" sx={{ fontSize: '11px', fontWeight: 700, color: '#6c757d', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px', display: 'block' }}>
                                    JUSTIFICATION
                                </Typography>
                                <TextareaAutosize
                                    key={`concept-justification-${concept.evaluationId || index}`}
                                    minRows={1}
                                    className={`concept-comment-area ${!concept.mentorEvaluation?.justification ? 'empty-readonly' : ''}`}
                                    value={concept.mentorEvaluation?.justification || ""}
                                    disabled={true}
                                    placeholder="No justification provided."
                                    style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '6px', padding: '16px', fontSize: '14px', color: '#6c757d', backgroundColor: '#e9ecef', lineHeight: 1.6, fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box', overflow: 'hidden' }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            ))}

            {/* Concept Performance Summary Card (Common) */}
            {perfSummary && (
                <Box className="concept-performance-summary-card" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', marginTop: '32px', backgroundColor: '#fff', overflow: 'hidden' }}>
                    <Box className="concept-summary-header" sx={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f8f9fa', padding: '14px 24px', borderBottom: '1px solid #e0e0e0' }}>
                        <CheckCircleOutlineIcon sx={{ color: '#1976d2' }} fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#495057', letterSpacing: '0.5px' }}>CONCEPT PERFORMANCE SUMMARY</Typography>
                    </Box>
                    <Box className="concept-summary-body" sx={{ padding: '24px 28px' }}>

                        <Typography component="div" sx={{ fontSize: '14.5px', fontWeight: 700, color: '#212529', marginBottom: '8px', display: 'block' }}>
                            Feedback Summary
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6, marginBottom: '24px', display: 'block' }}>
                            {perfSummary?.summaryText?.feedback || "No feedback summary available."}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1976d2', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
                                    <ThumbUpAltOutlinedIcon sx={{ fontSize: '18px' }} />
                                    WHAT WENT WELL
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {perfSummary?.summaryText?.doneWell?.length > 0 ? (
                                        perfSummary.summaryText.doneWell.map((item, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <CheckIcon sx={{ color: '#1976d2', fontSize: '16px' }} />
                                                <Typography sx={{ fontSize: '13.5px', color: '#495057' }}>{item}</Typography>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography sx={{ fontSize: '13.5px', color: '#6c757d' }}>No positive points identified.</Typography>
                                    )}
                                </Box>
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c5221f', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
                                    <TrendingUpIcon sx={{ fontSize: '18px' }} />
                                    AREAS FOR IMPROVEMENT
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {perfSummary?.summaryText?.improvementAreas?.length > 0 ? (
                                        perfSummary.summaryText.improvementAreas.map((item, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <ErrorOutlineIcon sx={{ color: '#c5221f', fontSize: '16px' }} />
                                                <Typography sx={{ fontSize: '13.5px', color: '#495057' }}>{item}</Typography>
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography sx={{ fontSize: '13.5px', color: '#6c757d' }}>No areas for improvement identified.</Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TechnicalConceptsTab;
